package pl.nickers.match3.ecs

import kotlin.random.Random

// ---------------------------------------------------------------------------
// Match combination system
// ---------------------------------------------------------------------------

/**
 * Priority constants used by [applyMatchGroups] to break ties when two
 * [MatchGroup]s would seed the same entity. Higher value wins.
 *
 * Phase 5 of the data-driven refactor: the old MatchKind enum is gone;
 * each strategy now reports a numeric priority and the piece id of the
 * tile it wants to seed (or null for a plain delete-only match).
 */
object MatchPriority {
    const val NORMAL = 0
    const val BOMB_2X2 = 1
    const val CROSS_BOMB = 2
    const val LASER = 3
    const val COLOURFUL_SWIRL = 4
}

/**
 * A set of entities that form a single match pattern.
 *
 * @param entities    all entity IDs participating in this match
 * @param seedIndex   index into [entities] for the entity that will be transformed into a
 *                    special tile; -1 when the match seeds no special tile (delete-only)
 * @param seedPieceId the piece id of the special tile to seed, or null for delete-only
 * @param priority    used to break seed conflicts when two groups want the same cell
 */
data class MatchGroup(
    val entities: List<Int>,
    val seedIndex: Int,
    val seedPieceId: String? = null,
    val priority: Int = MatchPriority.NORMAL,
) {
    val seed: Int get() = if (seedIndex >= 0) entities[seedIndex] else -1
}

private data class SpecialTileTransform(
    val pieceId: String,
    val priority: Int,
)

/**
 * Discovers all match groups on the current board by iterating [strategies] in order
 * and restarting from the highest-priority strategy after every match.
 *
 * Algorithm:
 * 1. Build a mutable grid snapshot (entity ID per cell, -1 for empty/special).
 * 2. Try each strategy in order; on the first hit, record the [MatchGroup] and
 *    erase its entities from the snapshot, then restart from strategy 0.
 * 3. Stop when no strategy finds anything.
 *
 * Because the loop always restarts from the top, a high-priority strategy can
 * never be "beaten" by a lower-priority one, and the same entity can never
 * appear in two [MatchGroup]s (it is erased from the snapshot after the first
 * group claims it).
 *
 * Bombs and ice cubes are excluded from the snapshot (same as [findMatchesOnGrid]).
 */
fun findMatchGroups(
    world: World,
    width: Int,
    height: Int,
    strategies: List<MatchStrategy> = defaultMatchStrategies,
    pieceSet: PieceSet = PieceSetLoader.default(),
): List<MatchGroup> {
    val posMapper = world.mapper<GridPositionComponent>()
    val typeMapper = world.mapper<JellyTypeComponent>()

    val grid = Array(height) { IntArray(width) { -1 } }
    val entityPos = HashMap<Int, Pair<Int, Int>>()

    fun isMatchable(entityId: Int): Boolean =
        world.isFamily(entityId, pieceSet, Family.JELLY)

    val aspect = Aspect.all(GridPositionComponent::class, JellyTypeComponent::class)
    world.getEntitiesForAspect(aspect).forEach { entityId ->
        if (!isMatchable(entityId)) return@forEach
        val pos = posMapper[entityId]!!
        if (pos.row in 0 until height && pos.col in 0 until width) {
            grid[pos.row][pos.col] = entityId
            entityPos[entityId] = pos.row to pos.col
        }
    }

    val results = mutableListOf<MatchGroup>()

    outer@ while (true) {
        for (strategy in strategies) {
            val match = strategy.findFirst(grid, typeMapper, width, height) ?: continue
            results.add(match)
            match.entities.forEach { entityId ->
                entityPos.remove(entityId)?.let { (r, c) -> grid[r][c] = -1 }
            }
            continue@outer
        }
        break
    }

    return results
}

/**
 * Resolves a list of [MatchGroup] objects: transforms seed entities into special tiles
 * and deletes the remaining matched entities, then applies gravity.
 *
 * @param scorableEntities when non-null, only deleted entities in this set earn score
 *        and goal-flight sprites — used by [MatchDetectionSystem] to withhold both from
 *        spoiled (cursed) match groups. Null means every deleted entity scores.
 * @return score delta (10 pts per scorable deleted entity, plus the Purified Rune +1 bonus;
 *         transformed seeds do not count here — the player earns their explosion score when
 *         they later activate the special tile)
 */
fun applyMatchGroups(
    world: World,
    groups: List<MatchGroup>,
    width: Int,
    height: Int,
    random: Random,
    catalog: EntityCatalog,
    preferredSeeds: Set<Int> = emptySet(),
    pieceSet: PieceSet = PieceSetLoader.default(),
    scorableEntities: Set<Int>? = null,
): Int {
    if (groups.isEmpty()) return 0

    val allMatched = groups.flatMapTo(mutableSetOf()) { it.entities }

    // Build seed→piece-id map; highest-priority piece wins when two groups share a seed.
    // Within each group we prefer a "just moved" entity (swapped or fallen) so the
    // resulting special tile appears where the player put their jelly.
    val seedsToTransform: Map<Int, SpecialTileTransform> = buildMap {
        for (group in groups) {
            val pieceId = group.seedPieceId ?: continue
            val preferred = group.entities.firstOrNull { it in preferredSeeds }
            val seed = preferred ?: group.seed
            if (seed != -1 && world.entityExists(seed)) {
                val existing = get(seed)
                if (existing == null || group.priority > existing.priority) {
                    put(seed, SpecialTileTransform(pieceId = pieceId, priority = group.priority))
                }
            }
        }
    }

    val entitiesToDelete = allMatched - seedsToTransform.keys

    for ((seedId, transform) in seedsToTransform) {
        if (world.entityExists(seedId)) {
            world.transformEntityToSpecialTile(
                entityId = seedId,
                pieceId = transform.pieceId,
                pieceSet = pieceSet,
                random = random,
            )
        }
    }

    val scorable = scorableEntities?.let { entitiesToDelete intersect it } ?: entitiesToDelete
    // Assumption for issue #107 / US6.2: keep the existing special-creation scoring rule.
    // A matched ingredient that transforms into a special tile is not removed now, so its
    // rune bonus is earned later only if/when that special is removed on a rune.
    val score = world.removalScore(scorable)
    applyGravityToGrid(world, entitiesToDelete, width, height, goalFlightEntities = scorable)
    return score
}

fun findMatchesOnGrid(
    world: World,
    width: Int,
    height: Int,
    pieceSet: PieceSet = PieceSetLoader.default(),
): Set<Int> =
    findMatchGroups(world, width, height, pieceSet = pieceSet).flatMapTo(mutableSetOf()) { it.entities }
